import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight } from "lucide-react";

const INDUSTRIES = ["AI/ML", "Fintech", "Healthcare", "SaaS", "Climate", "Deep Tech", "Consumer", "Enterprise", "Web3", "Biotech", "Robotics", "Education"];
const SKILLS = ["Engineering", "Product", "Design", "Sales", "Marketing", "Finance", "Operations", "AI/ML", "Data", "Growth", "Legal", "HR"];
const OPEN_TO = ["remote", "hybrid", "onsite"];

export default function Onboarding() {
  const { user, updateUser } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    headline: "",
    bio: "",
    country: "",
    city: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
    current_role: "",
    current_org: "",
    experience_years: 5,
    linkedin: "",
    github: "",
    website: "",
    skills: [],
    industries: [],
    open_to: ["remote"],
    leadership_style: "",
    work_style: "",
    communication_style: "",
    risk_appetite: "",
    stage_preference: "",
    mission: "",
  });

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleArr = (k, v) => setF((s) => ({
    ...s,
    [k]: s[k].includes(v) ? s[k].filter((x) => x !== v) : [...s[k], v],
  }));

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/profile", { ...f, onboarded: true });
      updateUser(data);
      toast.success("Profile ready");
      nav("/app");
    } catch (e) {
      toast.error("Failed to save profile");
    } finally {
      setBusy(false);
    }
  };

  const steps = ["Basics", "Expertise", user?.role_type === "founder" || user?.role_type === "executive" ? "DNA" : "Preferences", "Review"];
  const progress = ((step + 1) / steps.length) * 100;

  return (
    <div>
      <div className="mb-8">
        <div className="label-caps mb-2">Step {step + 1} of {steps.length}</div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-semibold">Welcome, {user?.full_name?.split(" ")[0]}</h1>
        <p className="text-muted-foreground mt-2">Let&apos;s build your presence on the tribunal.</p>
        <Progress value={progress} className="mt-6 h-1" data-testid="onboarding-progress" />
      </div>

      <Card className="p-6 md:p-10 max-w-3xl">
        {step === 0 && (
          <div className="space-y-4">
            <h2 className="text-xl font-medium mb-2">Basics</h2>
            <div>
              <Label>Headline</Label>
              <Input value={f.headline} onChange={(e) => set("headline", e.target.value)}
                placeholder="e.g. CTO scaling AI infrastructure" data-testid="ob-headline" className="mt-1.5" />
            </div>
            <div>
              <Label>Short bio</Label>
              <Textarea value={f.bio} onChange={(e) => set("bio", e.target.value)}
                rows={4} placeholder="A few sentences about you, your mission and what you're looking for."
                data-testid="ob-bio" className="mt-1.5" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Country</Label>
                <Input value={f.country} onChange={(e) => set("country", e.target.value)} data-testid="ob-country" className="mt-1.5" />
              </div>
              <div>
                <Label>City</Label>
                <Input value={f.city} onChange={(e) => set("city", e.target.value)} data-testid="ob-city" className="mt-1.5" />
              </div>
              <div>
                <Label>Current role</Label>
                <Input value={f.current_role} onChange={(e) => set("current_role", e.target.value)} data-testid="ob-role" className="mt-1.5" />
              </div>
              <div>
                <Label>Current organization</Label>
                <Input value={f.current_org} onChange={(e) => set("current_org", e.target.value)} data-testid="ob-org" className="mt-1.5" />
              </div>
              <div>
                <Label>Years of experience</Label>
                <Input type="number" min={0} value={f.experience_years}
                  onChange={(e) => set("experience_years", parseInt(e.target.value || "0"))}
                  data-testid="ob-exp" className="mt-1.5" />
              </div>
              <div>
                <Label>Timezone</Label>
                <Input value={f.timezone} onChange={(e) => set("timezone", e.target.value)} data-testid="ob-tz" className="mt-1.5" />
              </div>
              <div>
                <Label>LinkedIn</Label>
                <Input value={f.linkedin} onChange={(e) => set("linkedin", e.target.value)} data-testid="ob-linkedin" className="mt-1.5" />
              </div>
              <div>
                <Label>GitHub</Label>
                <Input value={f.github} onChange={(e) => set("github", e.target.value)} data-testid="ob-github" className="mt-1.5" />
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-6">
            <h2 className="text-xl font-medium">Expertise</h2>
            <TagPicker label="Skills" testid="ob-skills" options={SKILLS} selected={f.skills} onToggle={(v) => toggleArr("skills", v)} />
            <TagPicker label="Industries" testid="ob-industries" options={INDUSTRIES} selected={f.industries} onToggle={(v) => toggleArr("industries", v)} />
            <TagPicker label="Open to" testid="ob-opento" options={OPEN_TO} selected={f.open_to} onToggle={(v) => toggleArr("open_to", v)} />
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <h2 className="text-xl font-medium">
              {user?.role_type === "founder" ? "Founder DNA" : user?.role_type === "executive" ? "Executive DNA" : "Preferences"}
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <DNASelect label="Leadership style" value={f.leadership_style} testid="dna-lead"
                onChange={(v) => set("leadership_style", v)}
                options={["Visionary", "Servant", "Coaching", "Democratic", "Directive", "Transformational"]} />
              <DNASelect label="Work style" value={f.work_style} testid="dna-work"
                onChange={(v) => set("work_style", v)}
                options={["Async-first", "Deep focus", "Collaborative", "Fast iteration", "Structured"]} />
              <DNASelect label="Communication style" value={f.communication_style} testid="dna-comm"
                onChange={(v) => set("communication_style", v)}
                options={["Direct", "Consultative", "Written-heavy", "Verbal-heavy", "Data-driven"]} />
              <DNASelect label="Risk appetite" value={f.risk_appetite} testid="dna-risk"
                onChange={(v) => set("risk_appetite", v)}
                options={["Conservative", "Balanced", "Aggressive", "Contrarian"]} />
              <DNASelect label="Stage preference" value={f.stage_preference} testid="dna-stage"
                onChange={(v) => set("stage_preference", v)}
                options={["Pre-seed", "Seed", "Series A", "Series B+", "Growth", "Pre-IPO"]} />
            </div>
            <div>
              <Label>Mission you care about</Label>
              <Textarea value={f.mission} onChange={(e) => set("mission", e.target.value)}
                rows={3} placeholder="What problem do you want to spend the next decade on?"
                data-testid="dna-mission" className="mt-1.5" />
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <h2 className="text-xl font-medium mb-2">Review</h2>
            <p className="text-muted-foreground text-sm">Everything looks good. You can edit anything later from your profile.</p>
            <div className="rounded-lg border border-border p-4 mt-4 text-sm space-y-1">
              <div><span className="text-muted-foreground">Name:</span> {user?.full_name}</div>
              <div><span className="text-muted-foreground">Role:</span> {user?.role_type}</div>
              <div><span className="text-muted-foreground">Headline:</span> {f.headline || "—"}</div>
              <div><span className="text-muted-foreground">Location:</span> {[f.city, f.country].filter(Boolean).join(", ") || "—"}</div>
              <div><span className="text-muted-foreground">Skills:</span> {f.skills.join(", ") || "—"}</div>
              <div><span className="text-muted-foreground">Industries:</span> {f.industries.join(", ") || "—"}</div>
            </div>
          </div>
        )}

        <div className="mt-10 flex items-center justify-between">
          <Button variant="ghost" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="ob-back">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          {step < steps.length - 1 ? (
            <Button onClick={() => setStep(step + 1)} className="rounded-full" data-testid="ob-next">
              Continue <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={submit} disabled={busy} className="rounded-full" data-testid="ob-finish">
              {busy ? "Saving…" : "Finish and enter"}
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}

function TagPicker({ label, options, selected, onToggle, testid }) {
  return (
    <div>
      <Label className="mb-2 block">{label}</Label>
      <div className="flex flex-wrap gap-2" data-testid={testid}>
        {options.map((o) => {
          const active = selected.includes(o);
          return (
            <button type="button" key={o} onClick={() => onToggle(o)}
              data-testid={`${testid}-${o}`}
              className={`px-3.5 py-1.5 rounded-full text-sm border transition-colors duration-200 ${
                active
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border hover:border-primary/50"
              }`}>
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DNASelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="mt-1.5"><SelectValue placeholder="Select…" /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o} value={o} data-testid={`${testid}-${o}`}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
